import { useEffect } from "react";
import type { Application, Endpoints } from "../api";
import { applicationTypeLabel, formatDate, initials } from "../presentation";
import { ApplicationStatusBadge } from "./ApplicationStatusBadge";
import { Button, IconButton } from "./ui/Button";
import { CopyField } from "./ui/CopyField";
import { Icon } from "./ui/Icon";

interface ApplicationDetailDrawerProps {
  readonly application: Application;
  readonly endpoints: Endpoints | null;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onEdit: () => void;
  readonly onQuickstart: () => void;
  readonly onRotate: () => void;
  readonly onToggle: () => void;
}

/** Focused application workspace shown without losing table filter context. */
export function ApplicationDetailDrawer({
  application,
  endpoints,
  busy,
  onClose,
  onEdit,
  onQuickstart,
  onRotate,
  onToggle,
}: ApplicationDetailDrawerProps) {
  const isNative = application.appType === "native";
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("keydown", closeOnEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [busy, onClose]);

  return (
    <div className="drawer-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !busy) onClose();
    }}>
      <aside aria-labelledby="application-detail-title" aria-modal="true" className="detail-drawer" role="dialog">
        <header className="drawer-header">
          <div className="drawer-title-row">
            <span className={`application-avatar application-avatar-large type-${application.appType}`}>{initials(application.name)}</span>
            <div><p>{applicationTypeLabel(application.appType)}</p><h2 id="application-detail-title">{application.name}</h2></div>
          </div>
          <IconButton disabled={busy} icon="x" label="关闭详情" onClick={onClose} />
        </header>
        <div className="drawer-status-row"><ApplicationStatusBadge status={application.status} /><span>更新于 {formatDate(application.updatedAt)}</span></div>
        <div className="drawer-body">
          {application.lastError === null ? null : <div className="inline-alert"><strong>配置同步失败</strong><p>{application.lastError}</p></div>}
          <section className="detail-section">
            <div className="section-heading"><div><h3>基础信息</h3><p>应用身份与租户边界</p></div></div>
            <div className="copy-field-grid">
              <CopyField label={isNative ? "AppKey" : "App ID / Client ID"} value={application.appKey} />
              <CopyField label="Tenant ID" value={application.tenantId} />
              <CopyField label="REST API" value={endpoints?.api ?? "加载中…"} />
              <CopyField label="WebSocket" value={endpoints?.webSocket ?? "加载中…"} />
            </div>
          </section>
          <section className="detail-section">
            <div className="section-heading"><div><h3>接入配置</h3><p>{isNative ? "UserSig 与密钥信息" : "OIDC 回调和跨域白名单"}</p></div></div>
            {isNative ? (
              <dl className="definition-list">
                <div><dt>管理员 userID</dt><dd>{application.adminUserId}</dd></div>
                <div><dt>UserSig 有效期</dt><dd>{application.userSigExpireSeconds.toLocaleString("zh-CN")} 秒</dd></div>
                <div><dt>密钥版本</dt><dd>v{application.credential.version} · {application.credential.keyId ?? "未配置"}</dd></div>
              </dl>
            ) : (
              <div className="uri-groups">
                <UriList icon="globe" label="Redirect URI" values={application.redirectUris} />
                <UriList icon="activity" label="允许的 Origin" values={application.allowedOrigins} />
              </div>
            )}
          </section>
          <section className="detail-section detail-description">
            <div className="section-heading"><div><h3>应用描述</h3></div></div>
            <p>{application.description ?? "暂无应用描述。"}</p>
          </section>
        </div>
        <footer className="drawer-footer">
          <Button onClick={onEdit}>编辑配置</Button>
          <Button leadingIcon="code" onClick={onQuickstart}>接入代码</Button>
          {isNative ? <Button disabled={busy} leadingIcon="key" onClick={onRotate}>轮换密钥</Button> : null}
          <Button loading={busy} variant={application.enabled ? "danger" : "primary"} onClick={onToggle}>
            {application.enabled ? "停用应用" : "启用应用"}
          </Button>
        </footer>
      </aside>
    </div>
  );
}

function UriList({ icon, label, values }: {
  readonly icon: "activity" | "globe";
  readonly label: string;
  readonly values: readonly string[];
}) {
  return (
    <div className="uri-group">
      <h4><Icon name={icon} size={15} />{label}</h4>
      {values.length === 0 ? <p>未配置</p> : values.map((value) => <code key={value}>{value}</code>)}
    </div>
  );
}
